import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DropdownItem, DropdownMenu } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { SerializedAccountForList } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../common/types';
import { UserAvatar } from '../common/views/userAvatar/UserAvatar';
import { SMLUpgradeFeature } from '../music24/config/music24PlanCapabilities';
import { useNextPlanForFeature } from '../music24/hooks/useNextPlanForFeature';
import { ArtistSwitcherLimitBox } from './ArtistSwitcherLimitBox';
import { artistSwitcherMessages } from './artistSwitcherMessages';
import { countOwnArtists, isDemoAccount } from './demoAccounts';
import { isReadOnlyAccount } from './readOnlyAccounts';
import { ReadOnlyArtistTag } from './ReadOnlyArtistTag';
import { DemoArtistTag } from './DemoArtistTag';
/* eslint-disable css-modules/no-unused-class -- shared with ArtistSwitcherLimitBox.tsx; each file uses its own slice of it */
import styles from './ArtistSwitcherMenu.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistSwitcherMenuProps {
  accounts: SerializedAccountForList[];
  activeAccountId: number | null;
  /** `null` means unlimited (no count, no disabling). */
  artistsLimit: number | null;
  onSelect: (accountId: number) => void;
  onAddArtist: () => void;
  onUpgradeHint: () => void;
  /** Classes on the menu. Defaults to the width of the toggle. */
  className?: string;
  /** Align the menu with the right edge of the toggle (a pill at the far right of a bar). */
  end?: boolean;
  /** design: workspaces a1. The "Workspaces" part above the artists; undefined with one workspace (a2). */
  workspaceSection?: React.ReactNode;
  /** design: workspaces a3. The artists are dimmed and do not react while a workspace opens. */
  isDisabled?: boolean;
}

const ArtistSwitcherMenuComponent: React.FC<ArtistSwitcherMenuProps> = ({
  accounts,
  activeAccountId,
  artistsLimit,
  onSelect,
  onAddArtist,
  onUpgradeHint,
  className = 'w-100',
  end = false,
  workspaceSection,
  isDisabled = false,
}) => {
  const intl = useIntl();
  // design: music24-demo-artist a1. The demo artist does not count.
  const ownCount = useMemo(() => countOwnArtists(accounts) ?? 0, [accounts]);
  const atLimit = artistsLimit !== null && ownCount >= artistsLimit;
  const { current, nextPlan } = useNextPlanForFeature(SMLUpgradeFeature.artists);

  return (
    <DropdownMenu className={className} end={end}>
      {workspaceSection}
      {workspaceSection !== undefined && <DropdownItem divider />}
      <DropdownItem header>
        <span className={styles.headerRow}>
          {intl.formatMessage(artistSwitcherMessages.header)}
          {artistsLimit !== null && (
            <span className={styles.count}>
              {intl.formatMessage(artistSwitcherMessages.count, { count: ownCount, limit: artistsLimit })}
            </span>
          )}
        </span>
      </DropdownItem>
      {accounts.map((account) => (
        <DropdownItem
          key={account.id}
          onClick={() => onSelect(account.id)}
          active={account.id === activeAccountId}
          disabled={isDisabled}
          className={isDisabled ? styles.dimmed : undefined}
        >
          <span className={styles.row}>
            <UserAvatar size={22} url={account.avatarProcessedUrl} name={account.name} />
            <span className={styles.name}>{account.name}</span>
            {isDemoAccount(account) && <DemoArtistTag />}
            {isReadOnlyAccount(account) && <ReadOnlyArtistTag />}
            {account.id === activeAccountId && <FontAwesomeIcon icon={faCheck} />}
          </span>
        </DropdownItem>
      ))}
      <DropdownItem divider />
      <DropdownItem onClick={onAddArtist} disabled={atLimit || isDisabled}>
        <span className={styles.row}>
          <FontAwesomeIcon icon={faPlus} fixedWidth />
          {intl.formatMessage(artistSwitcherMessages.add)}
        </span>
      </DropdownItem>
      {atLimit && <ArtistSwitcherLimitBox current={current} nextPlan={nextPlan} onUpgrade={onUpgradeHint} />}
    </DropdownMenu>
  );
};

export const ArtistSwitcherMenu = TypedMemo(ArtistSwitcherMenuComponent);
